import React from 'react';
import Image from '@plone/volto/components/theme/Image/Image';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from './EditItemButton';
import {
  DESTAQUES_SCALES,
  getDestaqueImageProps,
  type DestaqueCard as DestaqueCardModel,
} from './helpers';

interface CardProps {
  card: DestaqueCardModel;
  isEditMode?: boolean;
  className?: string;
  /** Presente só no editor: abre este item na barra lateral. */
  onEditItem?: (index: number) => void;
}

/**
 * Lápis do card. Só aparece para itens curados: os do fallback automático de
 * notícias não existem em `data.items` e não há o que editar.
 */
const CardEditButton: React.FC<{
  card: DestaqueCardModel;
  onEditItem?: (index: number) => void;
  className?: string;
}> = ({ card, onEditItem, className }) => {
  if (!onEditItem || !card.curated) return null;
  return (
    <EditItemButton
      label={`Editar destaque: ${card.title || 'sem título'}`}
      onClick={() => onEditItem(card.sourceIndex)}
      className={className}
    />
  );
};

/**
 * Título-link do destaque.
 *
 * O `after:absolute after:inset-0` estende a área clicável para o card inteiro
 * sem inchar o nome acessível do link — o anel de foco continua desenhando em
 * volta do texto, que é o que o leitor de tela anuncia. Notícia do `@search`
 * usa `ConditionalLink` para não navegar no modo de edição.
 */
const DestaqueLink: React.FC<{
  card: DestaqueCardModel;
  isEditMode?: boolean;
  className: string;
  children: React.ReactNode;
}> = ({ card, isEditMode, className, children }) => {
  if (card.newsItem) {
    return (
      <MPFLink
        item={card.newsItem}
        isEditMode={isEditMode}
        className={className}
      >
        {children}
      </MPFLink>
    );
  }

  // Item curado sem conteúdo escolhido: um `<a>` sem `href` não é clicável nem
  // focável, e ainda assim seria anunciado como link. Melhor não fingir que é.
  if (!card.href) {
    return <span className={className}>{children}</span>;
  }

  // `external` deixou de decidir o destino da aba — quem decide é o
  // `isInternalURL` do UniversalLink. O campo continua valendo como pedido
  // explícito do editor para abrir fora, inclusive em link interno.
  return (
    <MPFLink
      href={card.href}
      openInNewTab={card.external || undefined}
      className={className}
    >
      {children}
    </MPFLink>
  );
};

/**
 * Véu de leitura sobre a foto: gradiente + desfoque com `mask-image` casada nas
 * mesmas paradas, para o blur sumir junto com o gradiente (senão sobra uma
 * quina nítida acima do texto). Mesma técnica de `ImageCard`.
 */
const Veil: React.FC = () => (
  <div
    className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.8)_0,rgba(0,0,0,0.5)_55%,transparent)] dark:bg-[linear-gradient(to_top,rgba(6,9,16,0.95)_0,rgba(6,9,16,0.6)_55%,transparent)] backdrop-blur-[2px] [-webkit-mask-image:linear-gradient(to_top,#000_0,#000_55%,transparent)] [mask-image:linear-gradient(to_top,#000_0,#000_55%,transparent)]"
    aria-hidden="true"
  />
);

/**
 * Selo de vídeo. Não é botão: quem trata o clique é o card em volta (um link,
 * nos destaques; o botão que abre o modal, em "Onde o MPF atua?").
 */
export const PlayBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/70 ${className ?? ''}`}
  >
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 text-white">
      <path d="M8 5.5v13l11-6.5-11-6.5z" />
    </svg>
  </span>
);

// `text-on-primary` (e não `text-white` nem `text-on-primary`): a âncora fica
// dentro do `h3` e sua própria cor vence a herdada, então precisa carregar o
// mesmo quase-branco. Antes isto era `text-on-primary`, que só funcionava porque
// o token não invertia; agora `--surface` escurece no alto contraste e o
// título sumiria sobre a foto. `--on-primary` é a escolha certa aqui porque
// fica constante nos dois temas — o véu sob o texto é sempre escuro.
// Sem sublinhado no hover — a área clicável já é o card inteiro, via `after:`.
// `hover:!text-on-primary`: o `a:hover { color: @linkHoverColor }` global do
// Semantic UI (azul) tem mais especificidade que a classe sozinha
// ((0,1,1) vs (0,1,0)) e repintava o título no hover; o `!` do Tailwind
// garante a vitória sem exigir seletor mais específico aqui.
const OVERLAY_LINK =
  'cursor-pointer rounded text-on-primary after:absolute after:inset-0 hover:!text-on-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/60';

const IMAGE_CLASS =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

// Título fora da foto (card da 2ª linha e item de texto do mobile): o link já
// vem no `--primary`, então o `a:hover` azul do Semantic UI não tem o que
// repintar e o `!` do `OVERLAY_LINK` é dispensável aqui.
const CARD_LINK =
  'cursor-pointer rounded text-primary after:absolute after:inset-0 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:text-night-100 dark:focus-visible:ring-white';

/**
 * Altura da primeira linha da grade: 352px (22rem) a partir do `md`, conforme o
 * design. No mobile cada card vira uma linha própria e a altura fixa cortaria
 * demais a foto, então lá vale a proporção.
 *
 * O corte é `md` (768px), e não `sm`: abaixo dele a grade some e os quatro
 * destaques secundários viram a lista de texto do `DestaquesMaisMobile`. Fosse
 * `sm`, a faixa ficaria sem layout entre 640 e 767px.
 */
export const ROW_1_HEIGHT = 'md:aspect-auto md:h-[22rem]';

/** Manchete da grade: foto sangrada, título e linha de apoio sobre o véu. */
export const DestaqueHeroCard: React.FC<CardProps> = ({
  card,
  isEditMode,
  className,
  onEditItem,
}) => {
  const imageProps = getDestaqueImageProps(card, DESTAQUES_SCALES.hero);

  return (
    <article
      className={`group relative aspect-[16/10] overflow-hidden rounded-lg bg-gray-800 dark:bg-night-700 ${ROW_1_HEIGHT} ${className ?? ''}`}
    >
      <CardEditButton
        card={card}
        onEditItem={onEditItem}
        className="absolute right-2 top-2"
      />
      {imageProps && (
        <Image
          {...imageProps}
          alt={card.alt}
          loading="eager"
          fetchpriority="high"
          className={IMAGE_CLASS}
        />
      )}
      {card.isVideo && <PlayBadge />}

      <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
        <Veil />
        {/* `text-on-primary` não inverte no alto contraste, que é o certo
            para texto sobre foto: o véu abaixo dele é escuro nos dois temas
            (17.6:1 sobre o véu a 80%). */}
        <div className="relative">
          {/* Sem `line-clamp`: o título aparece inteiro. O tamanho é decidido
              na edição, onde o campo tem contador (60 caracteres, a faixa que
              o Google exibe sem cortar) — e o bloco é `absolute bottom-0`, então
              um título longo cresce para cima sobre a foto sem empurrar layout.

              20px no mobile: 28px é o maior texto do site, e no celular ele
              sozinho já empurrava o resto para baixo da dobra. 20 é o teto
              tipográfico do Figma mobile da capa. Desktop inalterado. */}
          <h3 className="text-reading font-semibold text-on-primary md:text-h2">
            <DestaqueLink
              card={card}
              isEditMode={isEditMode}
              className={OVERLAY_LINK}
            >
              {card.isVideo && <span className="sr-only">Vídeo: </span>}
              {card.title}
            </DestaqueLink>
          </h3>
          {card.description && (
            <p className="mt-2 truncate text-base font-medium text-on-primary">
              {card.description}
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

/** Card alto ao lado da manchete: mesma altura dela a partir do `sm`. */
export const DestaqueTallCard: React.FC<CardProps> = ({
  card,
  isEditMode,
  className,
  onEditItem,
}) => {
  const imageProps = getDestaqueImageProps(card, DESTAQUES_SCALES.tall);

  return (
    <article
      className={`group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-800 dark:bg-night-700 ${ROW_1_HEIGHT} ${className ?? ''}`}
    >
      <CardEditButton
        card={card}
        onEditItem={onEditItem}
        className="absolute right-2 top-2"
      />
      {imageProps && (
        <Image
          {...imageProps}
          alt={card.alt}
          loading="lazy"
          className={IMAGE_CLASS}
        />
      )}
      {card.isVideo && <PlayBadge />}

      <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
        <Veil />
        <div className="relative">
          <h3 className="text-reading font-semibold text-on-primary line-clamp-3">
            <DestaqueLink
              card={card}
              isEditMode={isEditMode}
              className={OVERLAY_LINK}
            >
              {card.isVideo && <span className="sr-only">Vídeo: </span>}
              {card.title}
            </DestaqueLink>
          </h3>
        </div>
      </div>
    </article>
  );
};

/** Card da segunda linha: foto em cima, título abaixo (fora da imagem). */
export const DestaqueGridCard: React.FC<CardProps> = ({
  card,
  isEditMode,
  className,
  onEditItem,
}) => {
  const imageProps = getDestaqueImageProps(card, DESTAQUES_SCALES.card);

  return (
    <article className={`group relative min-w-0 ${className ?? ''}`}>
      <CardEditButton
        card={card}
        onEditItem={onEditItem}
        className="absolute right-2 top-2"
      />
      {/* `relative` próprio: a foto e o selo de vídeo se posicionam pela
          moldura da imagem, e não pelo card inteiro (que inclui o título). */}
      <div className="relative aspect-[16/10] overflow-hidden rounded-lg bg-gray-800 dark:bg-night-700">
        {imageProps && (
          <Image
            {...imageProps}
            alt={card.alt}
            loading="lazy"
            className={IMAGE_CLASS}
          />
        )}
        {card.isVideo && <PlayBadge />}
      </div>

      {/* 16/24 semibold no `--primary` (brand 500 = #0B0261, o
          `--primary-Primary` do Figma) — e não no `var(--brand)`, que é o
          `--mpf-blue` legado (#150063). */}
      <h3 className="mt-2 line-clamp-2 text-base font-semibold text-primary dark:text-night-100">
        <DestaqueLink card={card} isEditMode={isEditMode} className={CARD_LINK}>
          {card.isVideo && <span className="sr-only">Vídeo: </span>}
          {card.title}
        </DestaqueLink>
      </h3>
    </article>
  );
};

/**
 * Destaque só em texto, usado na lista do `DestaquesMaisMobile`.
 *
 * Abaixo de `md` os quatro destaques secundários perdem a foto: empilhados, os
 * cards com imagem faziam a faixa ocupar telas inteiras antes do resto da
 * página. Aqui sobra o que decide o clique — título e linha de apoio.
 *
 * É uma linha de lista, não um cartão: a moldura branca e os divisores são do
 * `<details>` em volta, como no `AcoesRapidasCard`.
 *
 * Continua `h3`, como os cards que substitui: a faixa é um `h2` (o
 * "Destaques e serviços" do `CapaView`) e o `<summary>` que abre esta lista não
 * é heading, então a árvore não muda entre mobile e desktop.
 */
export const DestaqueTextItem: React.FC<CardProps> = ({
  card,
  isEditMode,
  className,
}) => (
  /* Só recuo e área de clique: a moldura branca é do cartão em volta, como o
     `<a>` de cada linha do `AcoesRapidasCard`. O `relative` fica porque é ele
     que ancora o `after:inset-0` do link — que assim cobre esta linha e para
     na borda dela. */
  <article className={`group relative p-4 ${className ?? ''}`}>
    <h3 className="mb-0 text-base font-semibold text-primary dark:text-night-100">
      <DestaqueLink card={card} isEditMode={isEditMode} className={CARD_LINK}>
        {card.isVideo && <span className="sr-only">Vídeo: </span>}
        {card.title}
      </DestaqueLink>
    </h3>
    {card.description && (
      <p className="mb-0 mt-1 text-sm text-on-surface-low dark:text-night-300">
        {card.description}
      </p>
    )}
  </article>
);
